import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import Header from "@/components/Header";
import ShareModal from "@/components/ShareModal";
import { CheckCircle2, Sparkles, Loader2, Twitter, ShieldCheck } from "lucide-react";
import { devWarn } from "@/lib/log";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [polls, setPolls] = useState(0);
  const [block, setBlock] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    if (!sid) return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await http.get(`/payments/status/${sid}`);
        if (!alive) return;
        setStatus(data);
        if (data.payment_status === "paid") {
          // Fetch the newly-paid block for the celebration overlay.
          if (data.block_id) {
            try {
              const bres = await http.get("/pixels/mine");
              const found = (bres.data.blocks || []).find((b) => b.id === data.block_id);
              if (found && alive) {
                setBlock(found);
                setShareOpen(true);
              }
            } catch { /* silent */ }
          }
          return;
        }
        if (polls < 30) {
          setTimeout(() => setPolls((p) => p + 1), 2000);
        }
      } catch (err) {
        if (err?.response?.status && err.response.status !== 404) {
          devWarn("[payment] status poll failed:", err?.response?.status || err?.message);
        }
      }
    };
    tick();
  }, [sid, polls]);

  const paid = status.payment_status === "paid";

  // Pre-formatted tweet text (viral share trigger) — verbatim template.
  const shareUrl = block ? `${window.location.origin}/?claimed=${block.id}` : window.location.origin;
  const shareTemplate =
    "Just claimed my permanent piece of web real estate on the pixel-empires.com grid! Get your startup on the map before the blocks sell out.";
  const tweetText = encodeURIComponent(`${shareTemplate}\n\n${shareUrl}`);

  return (
    <div className="min-h-screen">
      <Header />
      <div className="hero-orb bg-emerald-500" style={{ width: 460, height: 460, top: -140, left: -100 }} />
      <main className="pt-40 pb-24 px-4 max-w-lg mx-auto text-center">
        <div className="glass-strong p-10" data-testid="payment-success-card">
          {paid ? (
            <>
              <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500" />
              <h1 className="mt-4 font-display text-4xl">Ad is live 🎉</h1>
              <p className="mt-2 text-[var(--fg-muted)]">
                Your pixel block is now on the grid.
              </p>
              <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 text-xs text-emerald-300 font-medium" data-testid="paid-lifetime-badge">
                <ShieldCheck className="w-3.5 h-3.5" /> Owned for LIFETIME · No renewals
              </div>

              <div className="mt-6 grid gap-2">
                <a
                  data-testid="celebrate-share-x"
                  href={`https://twitter.com/intent/tweet?text=${tweetText}`}
                  target="_blank" rel="noopener noreferrer"
                  className="btn-primary justify-center"
                >
                  <Twitter className="w-4 h-4" /> Share to X — tell the world
                </a>
                <button
                  data-testid="open-share-modal"
                  onClick={() => setShareOpen(true)}
                  className="btn-ghost justify-center"
                  disabled={!block}
                >
                  <Sparkles className="w-4 h-4" /> More share options
                </button>
              </div>

              <div className="mt-6 flex items-center justify-center gap-2">
                <Link to="/dashboard" className="btn-ghost" data-testid="go-dashboard">
                  Go to dashboard
                </Link>
                <Link to="/" className="btn-ghost">View grid</Link>
              </div>
            </>
          ) : (
            <>
              <Loader2 className="w-14 h-14 mx-auto animate-spin text-indigo-500" />
              <h1 className="mt-4 font-display text-3xl">Confirming payment…</h1>
              <p className="mt-2 text-[var(--fg-muted)]">
                Hang tight, this usually takes a couple seconds.
              </p>
              <p className="mt-3 text-xs font-mono text-[var(--fg-muted)]">Session: {sid?.slice(0, 24)}…</p>
            </>
          )}
        </div>
      </main>

      <ShareModal
        open={shareOpen && !!block}
        block={block}
        onClose={() => setShareOpen(false)}
      />
    </div>
  );
}
